<template>
  <div
    ref="videoPreview"
    :class="{ 'video-preview': true, isFullscreen }"
    element-loading-text="视频加载中"
    @mouseenter="toggleControl(true)"
    @mousemove="toggleControl(true)"
    @mouseleave="fading"
    @touchmove="toggleControl(true)"
    @touchend="fading"
  >
    <div v-if="videoError" class="videoError">
      视频加载异常，请检查网络后重试
      <el-button type="primary" @click="reLoad">重新加载</el-button>
    </div>
    <template v-else>
      <video
        ref="video"
        :src="url"
        :poster="poster"
        controlslist="nodownload"
        disablePictureInPicture
        preload="metadata"
        loop
        @click="togglePlay"
        @canplay="canplay"
        @ended="endedVideo"
        @error="onError"
        @timeupdate="timeupdate"
        @pause="pause"
        @dblclick="toggleVideoPlay"
      ></video>
      <!-- 底部控制栏 -->
      <div v-show="isActionBar" key="control" class="controls-wrap" :class="[showControl ? 'show' : 'hide']">
        <el-slider v-model="playProgress" class="play-progress" :show-tooltip="false" @change="onDurationChange" />
        <div class="controls">
          <div class="left">
            <span class="play-btn iconfont" @click="togglePlay">
              <svg-icon :icon-class="playing ? 'pause' : 'play'"></svg-icon>
            </span>
            <span class="playTime">
              {{ formatTime(currentTime) }} /
              {{ formatTime(videoDuration) }}
            </span>
          </div>
          <div class="right">
            <el-popover
              :teleported="false"
              placement="top"
              trigger="hover"
              :visible-arrow="false"
              popper-class="popper-speed"
              :append-to-body="false"
              :popper-options="popperOptions"
            >
              <template #reference>
                <span class="speed">倍速</span>
              </template>
              <ul>
                <li
                  v-for="item in speedList"
                  :key="item"
                  :class="{ active: parseFloat(speed) === parseFloat(item) }"
                  :command="item"
                  @click="onSpeedChange(item)"
                >
                  {{ item }} x
                </li>
              </ul>
            </el-popover>
            <el-popover
              :teleported="false"
              placement="top"
              trigger="hover"
              :visible-arrow="false"
              popper-class="popper-voice"
              :append-to-body="false"
              :popper-options="popperOptions"
            >
              <template #reference>
                <span :class="['voice-btn', 'baseiconfont', videoVoice > 0 ? 'icon-preparation-volume' : 'icon-a-soundturningoff']"></span>
              </template>
              <el-slider v-model="videoVoice" :show-tooltip="false" vertical height="100%" @change="onVoiceChange"></el-slider>
            </el-popover>
            <template v-if="isShow">
              <span v-if="!isFullscreen" class="fullscreen-btn iconfont" @click="fullscreen">
                <svg-icon icon-class="fullscreensmall"></svg-icon>
              </span>
              <span v-else class="fullscreen-btn iconfont" @click="exitSource">
                <svg-icon icon-class="exitfullscreen"></svg-icon>
              </span>
            </template>
          </div>
        </div>
      </div>
      <!-- 首次/重新播放 持久展示-->
      <div v-show="(!startPlay || endPlay)" :class="`center-play-btn iconfont`" @click="togglePlay">
        <svg-icon :icon-class="endPlay ? 'replay' : 'play'"></svg-icon>
      </div>
    </template>
  </div>
</template>

<script>
import throttle from "lodash.throttle";
import debounce from "lodash.debounce";
import screenfull from "@/utils/screenfull";
import Hls from "hls.js";

export default {
  name: "VideoPreview",
  props: {
    url: {
      type: String,
      default: ""
    },
    urlType: {
      type: String,
      default: ""
    },
    poster: {
      type: String,
      default: ""
    },
    isShow: {
      type: Boolean,
      default: false
    },
    isActionBar: {
      type: Boolean,
      default: true
    },
    autoPlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      popperOptions: {
        boundariesElement: "scrollParent"
      },
      speedList: ["2.0", "1.5", "1.25", "1.0", "0.5"],
      speed: "1.0", // 播放速度
      startPlay: false, // 第一次播放
      endPlay: false, // 播放结束
      playProgress: 0, // 播放进度
      playing: false, // 播放中
      showControl: false, // N秒隐藏控制栏
      videoReadyState: false, // 能否播放
      videoError: false, // 加载错误
      videoDuration: 0, // 总时长
      currentTime: 0, // 播放时长
      videoVoice: 100, // 音量
      loading: false, // 视频加载中
      hls: null,
      isFullscreen: false
    };
  },
  methods: {
    // 格式化时间
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    },

    // hls加载m3u8
    loadingM3u8() {
      if (!this.$refs.video) {
        return;
      }

      if (Hls.isSupported()) {
        const hlsInstance = new Hls();
        hlsInstance.loadSource(this.url);
        hlsInstance.attachMedia(this.$refs.video);
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {});
        this.hls = hlsInstance;
      } else if (this.$refs.video.canPlayType("application/vnd.apple.mpegURL")) {
        this.$refs.video.src = this.url;
        this.$refs.video.addEventListener("loadedmetadata", () => {
          this.$refs.video?.play();
        });
      }
    },

    setFullScreenFlag() {
      this.isFullscreen = screenfull.isFullscreen;
    },

    listenScreenEvent() {
      screenfull.on("change", this.setFullScreenFlag);
    },

    fullscreen() {
      if (!screenfull.isEnabled) {
        return;
      }
      screenfull.toggle(this.$refs.videoPreview);
      this.$emit("fullscreen");
    },

    exitSource() {
      if (screenfull.isEnabled && screenfull.isFullscreen) {
        screenfull.exit();
      }
      this.$emit("exitSource");
    },

    // 监听按键
    handleWatchKey(e) {
      const key = window.event ? e.keyCode : e.which;
      if (!this.isFullscreen) {
        return;
      }
      // 空格
      if (key === 32) {
        this.togglePlay();
      }
    },

    toggleVideoPlay() {
      setTimeout(() => {
        this.togglePlay();
      }, 100);
    },

    togglePlay() {
      if (!this.videoReadyState) {
        return;
      }
      if (!this.startPlay) {
        this.startPlay = true;
      }
      this.toggleControl(true);
      this.playing ? this.pause() : this.play();
    },

    toggleControl(val) {
      // console.log("toggleControl", val, type);
      if (!this.videoReadyState) {
        return;
      }
      if (!this.startPlay || this.endPlay || this.videoError) {
        return;
      }
      this.showControl = val == null ? !this.showControl : val;
      this.fading();
    },

    async play() {
      const videoRef = this.$refs.video;
      if (!videoRef) {
        return;
      }
      try {
        videoRef.muted = false;
        await videoRef.play();
      } catch (error) {
        try {
          videoRef.volume = 0;
          this.videoVoice = 0;
          await videoRef.play();
        } catch (error) {
          this.pause();
          this.startPlay = false;
          this.loading = false;
          return;
        }
      }
      this.counting();
      this.playing = true;
      this.endPlay = false;
    },

    pause() {
      if (!this.$refs.video) {
        return;
      }

      this.$emit("pause", this.$refs.video);
      this.$refs.video.pause();
      this.playing = false;
    },

    canplay() {
      if (!this.$refs.video) {
        return;
      }

      this.loading = false;
      this.videoReadyState = true;
      this.videoDuration = Math.floor(this.$refs.video.duration || 0);
      this.$emit("ready", { duration: this.videoDuration });
      this.videoVoice = (this.$refs.video.volume || 1) * 100;
      this.speed = this.speedList.find((item) => parseFloat(item) === (this.$refs.video?.playbackRate || 1)) || "1.0";
      // 如果设置了自动播放，则自动开始播放
      if (this.autoPlay && !this.playing) {
        this.startPlay = true;
        this.play();
      }
    },

    reLoad() {
      this.videoError = false;
      this.loading = true;
    },

    onError() {
      this.videoError = true;
      this.loading = false;
      this.videoReadyState = false;
    },

    onVoiceChange() {
      if (!this.$refs.video) {
        return;
      }
      this.$refs.video.volume = this.videoVoice / 100;
    },

    // 播放结束改成暂停
    endedVideo() {
      this.pause();
      this.endPlay = true;
      this.$emit("ended");
    },

    // 修改进度
    onDurationChange(e) {
      if (!this.$refs.video) {
        return;
      }

      this.currentTime = (e / 100) * this.videoDuration;
      this.$refs.video.currentTime = this.currentTime;
    },

    // 控制栏消失倒计时
    fading: debounce(function () {
      // 暂停时不收起
      if (!this.playing) {
        return;
      }
      this.showControl && (this.showControl = false);
      // 关掉 popper
      const elements = this.$refs.videoPreview?.querySelectorAll(".el-popover") || [];
      for (const ele of elements) {
        ele.style.display = "none";
      }
    }, 4000),

    // 卡顿倒计时
    counting: debounce(function () {
      // N秒后如果还在播放，但又没有更新进度，重新调用 counting 就视为卡顿
      this.playing && (this.loading = true);
    }, 1100),

    // 更新进度
    timeupdate: throttle(function (e) {
      this.loading = false;
      this.counting();
      const target = e.target;
      this.currentTime = target?.currentTime || 0;
      this.playProgress = (this.currentTime / this.videoDuration) * 100;
    }, 1000),

    onSpeedChange(e) {
      if (!this.$refs.video) {
        return;
      }

      this.speed = e;
      this.$refs.video.playbackRate = parseFloat(e);
    }
  },
  mounted() {
    this.loading = true;
    document.addEventListener("keydown", this.handleWatchKey);
    this.listenScreenEvent();

    if (this.urlType === "m3u8") {
      this.loadingM3u8();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleWatchKey);
    screenfull.off("change", this.setFullScreenFlag);
    if (this.hls) {
      this.hls.destroy();
    }
  }
};
</script>

<style scoped lang="scss">
  .fade-enter,
  .temp-fade-leave-to {
    opacity: 0;
  }

  .video-preview {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
    font-size: 14px;
    ::v-deep .el-loading-spinner .el-loading-text {
      font-size: 14px;
      white-space: nowrap;
    }
    &.isFullscreen {
      font-size: 16px;
      ::v-deep .popper-speed {
        transform: translateX(30px);
      }
      ::v-deep .popper-voice {
        transform: translateX(50%);
      }
    }
  }
  .videoError {
    background-color: rgba(0, 0, 0, 0.8);
    height: 100%;
    flex-direction: column;
    display: flex;
    align-items: center;
    justify-content: center;

    color:#fff;
    .el-button {
      margin-top: 1em;
      display: block;
    }
  }
  video {
    margin: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
    outline: none !important;
    &:focus {
      outline: none !important;
    }
  }
  .center-play-btn {
    cursor: pointer;
    width: 5em;
    height: 5em;
    .svg-icon {
      display: block;
      width: 2.5em;
      height: 2.5em;
      margin: 1.25em auto;
    }
    &:before {
      display: block;
      font-size: 2.5em;
    }
    line-height: 2;
    font-size: 1em;
    color: #fff;
    text-align: center;
    white-space: nowrap;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .loading-img {
    display: block;
    width: 2em;
    height: 2em;
    margin: 1.5em;
    animation: rotate 1s linear infinite;
  }
  .controls-wrap {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    font-size: 1em;
    transition: transform ease 1s;
    z-index: 1;
    ::v-deep .hide {
      visibility: visible;
      transform: translateY(3.4285em);
      .el-slider__button {
        display: none;
      }
    }
    ::v-deep > .el-slider {
      margin-bottom: -16px;
      height: 24px;
      .el-slider__runway {
        margin: 0;
        border-radius: 0;
        height: 6px;
      }
      .el-slider__button {
        border-color: #1f82ff;
        width: 20px;
        height: 20px;
      }
      .el-slider__bar {
        border-radius: 0;
        background-color: #1f82ff;
        height: 6px;
      }
      .el-slider__button-wrapper {
        height: 36px;
        width: 36px;
        top: -15px;
      }
    }
    .controls {
      color: #fff;
      width: 100%;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: rgba(0, 0, 0, 0.4);
      .iconfont {
        cursor: pointer;
      }
      .playTime {
        font-family: '苹方-简';
        font-size: 14px;
        font-weight: normal;
        line-height: 14px;
        letter-spacing: normal;
        color: #ffffff;
      }
      .play-btn {
        font-size: 16px;
        margin: 0 24px 0 32px;
      }
      .voice-btn {
        font-size: 16px;
        margin: 0 24px;
      }
      .fullscreen-btn {
        font-size: 14px;
        margin: 0 32px 0 0;
      }
      .exitBtn {
        margin-right: 24px;
      }
      .el-divider {
        margin: 0;
        height: 32px;
      }
      .right {
        display: flex;
        align-items: center;
      }
      .btn-text {
        margin-left: 4px;
        font-size: 16px;
      }
    }
  }
</style>

<style lang="scss">
  .popper-voice {
    min-width: 2em !important;
    width: auto !important;
    flex-direction: column;
    align-items: center;
    display: flex;
    box-shadow: 0 0.143em 0.571em 0 rgba(0, 0, 0, 0.1);
    border: none;
    color: #ffffff;
    .el-slider {
      height: 10em;
    }
    .el-slider__bar {
      background-color: #1f82ff;
    }
    .el-slider__button {
      border-color: #1f82ff;
    }
  }
  .popper-speed {
    padding: 0 0.857em;
    min-width: auto !important;
    width: auto !important;
    color: #262626;
    font-size: 1em;
    box-shadow: 0 0.143em 0.571em 0 rgba(0, 0, 0, 0.1);
    border: none;
    outline: none;
    .speed {
      display: block;
      margin: 0 10px;
    }
    ul {
      list-style: none;
    }
    li {
      line-height: 2em;
      cursor: pointer;
      &.active {
        color: #fb7320;
      }
    }
  }
</style>